import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import apiService from '../services/api';
import GlobalSearchDropdown from './GlobalSearchDropdown';

/**
 * GlobalSearch Component
 * Provides global search functionality across all entity types
 */
const GlobalSearch = ({ navigate }) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState({
    clients: [],
    appointments: [],
    technicians: [],
    services: [],
    locations: [],
    leads: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Debounce the query - 500ms for better mobile UX
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 500);

    return () => clearTimeout(timer);
  }, [query]);

  // Perform search when debounced query changes
  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      performSearch(debouncedQuery);
    } else {
      setResults({
        clients: [],
        appointments: [],
        technicians: [],
        services: [],
        locations: [],
        leads: []
      });
      setShowDropdown(false);
      setError(null);
    }
  }, [debouncedQuery]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * Perform parallel searches across all entity types
   */
  const performSearch = async (searchQuery) => {
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);
    setShowDropdown(true);
    setSelectedIndex(-1);

    try {
      // Parallel API calls to all endpoints
      const [
        clientsData,
        appointmentsData,
        staffData,
        servicesData,
        locationsData,
        interactionsData
      ] = await Promise.allSettled([
        apiService.getClients({ search: searchQuery, limit: 5 }),
        apiService.getAppointments({ search: searchQuery, limit: 5 }),
        apiService.getStaff({ search: searchQuery, limit: 5 }),
        apiService.getServices({ search: searchQuery, limit: 5 }),
        apiService.getLocations({ search: searchQuery, limit: 5 }),
        apiService.get('/amn/persons/', { search: searchQuery, page_size: 5 })
      ]);

      // Extract results, handling failures gracefully
      const clientsArray = clientsData.status === 'fulfilled' ? (Array.isArray(clientsData.value) ? clientsData.value : clientsData.value.results || []) : [];

      // Sort clients by total_spent descending (highest spenders first)
      const sortedClients = clientsArray.sort((a, b) => {
        const aSpent = parseFloat(a.total_spent || 0);
        const bSpent = parseFloat(b.total_spent || 0);
        return bSpent - aSpent;
      });

      const newResults = {
        clients: sortedClients,
        appointments: appointmentsData.status === 'fulfilled' ? (Array.isArray(appointmentsData.value) ? appointmentsData.value : appointmentsData.value.results || []) : [],
        technicians: staffData.status === 'fulfilled' ? (Array.isArray(staffData.value) ? staffData.value : staffData.value.results || []).filter(s => s.role === 'technician' || s.is_technician) : [],
        services: servicesData.status === 'fulfilled' ? (Array.isArray(servicesData.value) ? servicesData.value : servicesData.value.results || []) : [],
        locations: locationsData.status === 'fulfilled' ? (Array.isArray(locationsData.value) ? locationsData.value : locationsData.value.results || []) : [],
        leads: interactionsData.status === 'fulfilled' ? (Array.isArray(interactionsData.value) ? interactionsData.value : interactionsData.value.results || []) : []
      };

      setResults(newResults);

      // Check if all requests failed
      const allFailed = [
        clientsData,
        appointmentsData,
        staffData,
        servicesData,
        locationsData,
        interactionsData
      ].every(result => result.status === 'rejected');

      if (allFailed) {
        setError('Failed to fetch search results. Please try again.');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError('An error occurred while searching. Please try again.');
        console.error('Search error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle result selection
   */
  const handleSelectResult = (item, entityType) => {
    // Navigate to appropriate page
    switch (entityType) {
      case 'clients':
        navigate(`/clients/${item.id}`);
        break;
      case 'appointments':
        navigate(`/appointments/${item.id}`);
        break;
      case 'technicians':
        navigate(`/technicians/${item.id}`);
        break;
      case 'services':
        // Services page doesn't have detail view, just navigate to services page
        navigate('/services');
        break;
      case 'locations':
        navigate(`/locations/${item.id}`);
        break;
      case 'leads':
        navigate(`/leads/${item.id}`);
        break;
      default:
        break;
    }

    // Close dropdown and optionally clear search
    setShowDropdown(false);
    setSelectedIndex(-1);
    // Optionally clear the query
    // setQuery('');
  };

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (e) => {
    if (!showDropdown) return;

    // Flatten results for navigation
    const flatResults = [];
    Object.entries(results).forEach(([entityType, items]) => {
      items.forEach((item) => {
        flatResults.push({ ...item, entityType });
      });
    });

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < flatResults.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;

      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < flatResults.length) {
          const selected = flatResults[selectedIndex];
          handleSelectResult(selected, selected.entityType);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        setSelectedIndex(-1);
        searchInputRef.current?.blur();
        break;

      default:
        break;
    }
  };

  /**
   * Handle clear button
   */
  const handleClear = () => {
    setQuery('');
    setDebouncedQuery('');
    setShowDropdown(false);
    setSelectedIndex(-1);
    setResults({
      clients: [],
      appointments: [],
      technicians: [],
      services: [],
      locations: [],
      leads: []
    });
    searchInputRef.current?.focus();
  };

  /**
   * Retry search on error
   */
  const handleRetry = () => {
    if (debouncedQuery.length >= 2) {
      performSearch(debouncedQuery);
    }
  };

  return (
    <div className="flex-1 max-w-md relative" ref={dropdownRef}>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (query.length >= 2) {
              setShowDropdown(true);
            }
          }}
          className="w-full pl-8 pr-8 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800 focus:border-transparent"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {showDropdown && (
        <GlobalSearchDropdown
          results={results}
          loading={loading}
          error={error}
          query={debouncedQuery}
          onSelectResult={handleSelectResult}
          onRetry={handleRetry}
          selectedIndex={selectedIndex}
        />
      )}
    </div>
  );
};

export default GlobalSearch;
